import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import omit from 'lodash/omit'
import forOwn from 'lodash/forOwn'
import { History } from 'history'

import { fetchProductPackages } from '../products/productActions'
import {
  previousPage,
  goToPage,
  nextPage,
} from '../../helpers/paginationHelper'
import {
  selectProductPackageIndex,
  selectProductPackageMeta,
} from '../../reducers/productPackages'
import {
  parseQuery,
  stringifyQuery,
  toggleSort,
} from '../../helpers/queryHelper'
import { redirectToSingleResult } from '../../helpers/indexPageHelper'
import { displayNotification } from '../../actions/notificationsActions'
import { User } from '../../core/types'
import IndexHeaderRow from '../common/indexes/indexHeaderRow'

import Breadcrumb from '../../components/Breadcrumb'
import TablePagination from '../../components/tablePagination'
import FilterBadge from '../../components/filterBadge'
import moment from 'moment'
import LoadingSection from '../../components/loadingSection'
import { calculateSurcharges } from '../../helpers/calculateSurcharges'
import { calculateProductPricing } from '../../helpers/calculateProductPricing'
import {
  hasNarrowingFilters,
  PRODUCTS_DEFAULT_WINDOW_DAYS,
} from './productHelpers'

export { hasNarrowingFilters }

export type Callback<T = void> = (...args: any) => T

export interface Props {
  currentUsers: User[]
  location: { key: any } & Location
  history: History
  displayNotification: Callback
  /** Dispatches fetchProductPackages thunk and returns its Promise */
  fetchProductPackages: Callback<Promise<any>>
  meta: { total: number }
}

interface ProductIndexState {
  loading: boolean
}

export class ProductIndex extends Component<Props, ProductIndexState> {
  static displayName = 'ProductIndex'

  state: ProductIndexState = {
    loading: true,
  }

  componentDidMount() {
    this.loadProducts()
  }

  componentDidUpdate(prevProps) {
    const {
      currentUsers,
      history,
      displayNotification,
      location: { search },
    } = this.props

    if (search !== prevProps.location.search) {
      this.loadProducts()
    }

    if (process.env.NODE_ENV !== 'test') {
      redirectToSingleResult(
        currentUsers,
        '/products',
        history,
        displayNotification
      )
    }
  }

  loadProducts = async () => {
    const { location, history } = this.props
    const defaultStatuses = [
      'booked',
      'empty',
      'selected',
      'quoted',
      'signed',
      'payment_succeeded',
      'payment_failed',
    ]
    const ninetyDaysAgo = moment()
      .subtract(PRODUCTS_DEFAULT_WINDOW_DAYS, 'days')
      .startOf('day')
      .format('YYYY-MM-DDTHH:mm:ss Z')

    if (!location.search) {
      const nav =
        typeof history.replace === 'function' ? history.replace : history.push
      nav({
        pathname: '/products',
        search: stringifyQuery({
          offset: 0,
          limit: 25,
          sort: 'UPDATED_AT',
          direction: 'DESC',
          productPackage: defaultStatuses,
          updatedAtAfter: ninetyDaysAgo,
        }),
      })
      return
    }

    // No narrowing filter applied: inject the default 90-day window so the
    // dashboard does not run an unbounded query over full history. Skip when
    // the user has already narrowed the dataset (any non-pagination filter),
    // and skip if a date window is already present to avoid a redirect loop.
    const parsedQuery: any = parseQuery(location.search)
    if (!hasNarrowingFilters(parsedQuery) && !parsedQuery.updatedAtAfter) {
      const nav =
        typeof history.replace === 'function' ? history.replace : history.push
      nav({
        pathname: '/products',
        search: stringifyQuery({ ...parsedQuery, updatedAtAfter: ninetyDaysAgo }),
      })
      return
    }

    await this.fetchProductPackagesWithFilters(location.search)
  }

  fetchProductPackagesWithFilters = async (query) => {
    const { fetchProductPackages } = this.props
    const parsedQuery: any = parseQuery(query)
    this.setState({ loading: true })
    try {
      // ensure some filters are always sent as strings
      for (const f of ['address', 'zip', 'vin', 'customerCodeContains']) {
        if (parsedQuery[f] != null && typeof parsedQuery[f] !== 'string') {
          parsedQuery[f] = String(parsedQuery[f])
        }
      }
      if (parsedQuery.phone || parsedQuery.email) {
        parsedQuery.phone = parsedQuery.phone
          ? JSON.stringify(parsedQuery.phone)
          : null
        parsedQuery.email =
          parsedQuery.email && typeof parsedQuery.email !== 'string'
            ? JSON.stringify(parsedQuery.email)
            : parsedQuery.email
      }
      await fetchProductPackages(parsedQuery)
    } finally {
      this.setState({ loading: false })
    }
  }

  headerClick = (sortEnum) => {
    const {
      location: { search },
    } = this.props
    this.props.history.push({
      pathname: '/products',
      search: toggleSort(search, sortEnum, 'DESC'),
    })
  }

  prevPageClick = () => {
    const {
      location: { search },
    } = this.props
    previousPage(search, this.props.history, '/products')
  }

  onPageClick = (pageNum) => {
    const {
      location: { search },
    } = this.props
    goToPage(search, this.props.history, pageNum, '/products')
  }

  nextPageClick = () => {
    const {
      currentUsers,
      location: { search },
    } = this.props
    nextPage(search, this.props.history, currentUsers.length, '/products')
  }

  filtersClick = () => {
    const {
      location: { search },
    } = this.props
    this.props.history.push({
      pathname: '/products/filters',
      search,
    })
  }

  countFilters = (query, defaultFilters) => {
    let count = 0

    forOwn(omit(query, defaultFilters), (obj) => {
      if (Array.isArray(obj)) {
        count += (obj as any[]).length
      } else {
        count += 1
      }
    })

    return count
  }

  filterBadge = () => {
    const {
      location: { search },
    } = this.props
    const parsedQuery = parseQuery(search)
    const defaultFilters = ['offset', 'limit']
    const count = this.countFilters(parsedQuery, defaultFilters)

    if (count > 0 && count < 10) {
      return <span className="index-table__filter-badge">{count}</span>
    } else if (count >= 10) {
      return (
        <span className="index-table__filter-badge index-table__filter-badge--multi">
          {count}
        </span>
      )
    } else {
      return <span className="index-table__filter-icon icon-filters" />
    }
  }

  openUser = (id) => {
    window.location.assign(`/users/${id}`)
  }

  render() {
    const { currentUsers, meta, location } = this.props
    const { total } = meta
    const { search } = location
    const parsedSearch = parseQuery(search)
    const totalPages = Math.ceil(total / parsedSearch.limit)

    const levels = [
      { label: 'Index ', path: '/' },
      {
        label: 'Protection Product Quotes',
        path: '/products',
      },
    ]

    const headerList = [
      { displayHeader: 'NAME', sortable: true, sortEnum: 'LAST_FIRST' },
      { displayHeader: 'ADDRESS', sortable: true, sortEnum: 'ADDRESS' },
      { displayHeader: 'CITY', sortable: true, sortEnum: 'CITY' },
      { displayHeader: 'STATE', sortable: true, sortEnum: 'STATE' },
      { displayHeader: 'ZIP', sortable: true, sortEnum: 'ZIP' },
      { displayHeader: 'VEHICLE', sortable: true, sortEnum: 'YEAR_MAKE_MODEL' },
      { displayHeader: 'LAST DATE', sortable: true, sortEnum: 'LAST_DATE' },
      { displayHeader: 'STATUS', sortable: true, sortEnum: 'STATUS' },
      { displayHeader: 'PRODUCT', sortable: true, sortEnum: 'PRODUCT' },
      { displayHeader: 'AGENT', sortable: true, sortEnum: 'AGENT_LAST_FIRST' },
    ]

    const rows = currentUsers.map((user, index) => {
      const {
        id,
        originalUserId,
        firstName,
        lastName,
        addresses,
        productPackage,
      } = user
      const address = (addresses || [])[0]
      const { line1, line2, city, state, zip } = address || {}

      const selectedProduct = productPackage?.products.find(
        (p) => !!p.selectedProductOption
      )

      const selectedOption = selectedProduct?.selectedProductOption

      // Calculate the correct price with discount and surcharges
      let selectedOptionPrice: string | null = null

      if (selectedOption) {
        // Calculate surcharges values first
        const surchargesValues = calculateSurcharges(
          selectedOption.surcharges || [],
          selectedOption.selectedSurchargeCodes || [],
          'Price',
          'MonthlyPayment'
        )

        const { totalCost } = calculateProductPricing({
          total_cost: selectedOption.totalPrice || 0,
          monthly_payment: 0, // Not used for this display
          surchargesValues,
          discount: productPackage.discount,
          coverage_months: 1, // Not used for total price calculation
        })

        selectedOptionPrice = Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(totalCost)
      }

      return (
        <tr
          className="index-table__row"
          data-testid="userIndexRow"
          key={index}
          onClick={() => this.openUser(originalUserId || id)}
        >
          <td className="index-table__cell">{`${lastName}, ${firstName}`}</td>
          {address ? (
            <td className="index-table__cell">
              {line1} {line2}
            </td>
          ) : (
            <td />
          )}
          {address ? <td className="index-table__cell">{city}</td> : <td />}
          {address ? <td className="index-table__cell">{state}</td> : <td />}
          {address ? <td className="index-table__cell">{zip}</td> : <td />}
          {productPackage ? (
            <td className="index-table__cell">
              {`${productPackage.vehicle.year} ${productPackage.vehicle.make} ${productPackage.vehicle.model}`}
            </td>
          ) : (
            <td />
          )}
          {productPackage?.updatedAt ? (
            <td className="index-table__cell">
              {moment(productPackage.updatedAt).format('MM/DD/YYYY')}
            </td>
          ) : (
            <td />
          )}
          {productPackage ? (
            <td className="index-table__cell">
              {productPackage.status.toUpperCase()}
            </td>
          ) : (
            <td />
          )}
          {selectedOption ? ( // todo: should only show selected product
            <td className="index-table__cell">
              {selectedOption.description} at {selectedOptionPrice}
            </td>
          ) : (
            <td />
          )}
          {productPackage?.admin ? (
            <td className="index-table__cell">
              {`${productPackage.admin.lastName}, ${productPackage.admin.firstName}`}
            </td>
          ) : (
            <td />
          )}
        </tr>
      )
    })

    return (
      <>
        <Breadcrumb levels={levels} />
        <LoadingSection
          isLoading={
            process.env.NODE_ENV === 'test' ? false : this.state.loading
          }
        >
          <div className="index index__drive-america" key={location.key}>
            <h1 className="index-table__header">
              Products Dashboard
              <span className="index-table__header--total">{total}</span>
            </h1>
            <div className="index-table__drive-america">
              <a
                data-testid="FiltersLink"
                onClick={this.filtersClick}
                className="index-table__filter-button"
              >
                <FilterBadge renderProps={this.props} />
                filters
              </a>
              <a
                href="/refiapp"
                className="button button-drive-america link-drive-america link-drive-america__new-applicant"
              >
                New Customer
              </a>
            </div>

            <table className="index-table">
              <IndexHeaderRow
                headerList={headerList}
                parsedSearch={parsedSearch}
                clickCallback={this.headerClick}
              />
              <tbody className="index-table__body">{rows}</tbody>
            </table>
            <TablePagination
              search={location.search}
              queryLength={currentUsers.length}
              totalPages={totalPages}
              prevPageClick={this.prevPageClick}
              onPageClick={this.onPageClick}
              nextPageClick={this.nextPageClick}
            />
          </div>
        </LoadingSection>
      </>
    )
  }
}

export const mapStateToProps = (state) => {
  return {
    currentUsers: selectProductPackageIndex(state.productPackages),
    meta: selectProductPackageMeta(state.productPackages),
  }
}

export default withRouter(
  connect(mapStateToProps, {
    fetchProductPackages,
    displayNotification,
  })(ProductIndex)
)
